import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
// import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { FormSpacer } from "@saleor/components/FormSpacer";
import Hr from "@saleor/components/Hr";
import MultiAutocompleteSelectField, {
  MultiAutocompleteChoiceType
} from "@saleor/components/MultiAutocompleteSelectField";
import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType
} from "@saleor/components/SingleAutocompleteSelectField";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { ChangeEvent } from "@saleor/hooks/useForm";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { FetchMoreProps } from "@saleor/types";
import { getFormErrors, getProductErrorMessage } from "@saleor/utils/errors";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useCatDetailsQuery } from "../../../categories/queries";
import { ProductDetails_product_brand } from "../../types/ProductDetails";
interface ProductType {
  hasVariants: boolean;
  id: string;
  name: string;
}

interface ProductOrganizationProps {
  brand?: ProductDetails_product_brand;
  canChangeCategory: boolean;
  canChangeType: boolean;
  categories?: SingleAutocompleteChoiceType[];
  categoryInputDisplayValue: string;
  collections?: MultiAutocompleteChoiceType[];
  collectionsInputDisplayValue: MultiAutocompleteChoiceType[];
  data: {
    brandLabel: string;
    category: string;
    collections: string[];
    productType?: ProductType;
  };
  disabled: boolean;
  disabledCollection: boolean;
  errors: ProductErrorFragment[];
  productType?: ProductType;
  productTypeInputDisplayValue?: string;
  productTypes?: SingleAutocompleteChoiceType[];
  validate: {
    category: null;
  };
  fetchCategories: (query: string) => void;
  fetchCollections: (query: string) => void;
  fetchMoreCategories: FetchMoreProps;
  fetchMoreCollections: FetchMoreProps;
  fetchMoreProductTypes?: FetchMoreProps;
  fetchProductTypes?: (data: string) => void;
  onCategoryChange: (event: ChangeEvent) => void;
  onCollectionChange: (event: ChangeEvent) => void;
  onProductTypeChange?: (event: ChangeEvent) => void;
  isStoreAdmin?: boolean;
}

const ProductOrganization: React.FC<ProductOrganizationProps> = props => {
  const {
    brand,
    canChangeCategory,
    canChangeType,
    categories,
    categoryInputDisplayValue,
    collections,
    collectionsInputDisplayValue,
    data,
    disabled,
    disabledCollection,
    errors,
    fetchCategories,
    fetchCollections,
    fetchMoreCategories,
    fetchMoreCollections,
    fetchMoreProductTypes,
    fetchProductTypes,
    productType,
    productTypeInputDisplayValue,
    productTypes,
    onCategoryChange,
    onCollectionChange,
    onProductTypeChange,
    isStoreAdmin,
    validate
  } = props;
  const { user } = useUser();
  const useStyles = makeStyles(
    theme => ({
      card: {
        overflow: "visible"
      },
      cardSubtitle: {
        fontSize: "1rem",
        marginBottom: theme.spacing(0.5)
      },
      hideLabel: {
        display: isStoreAdmin ? "none" : "block"
      },
      label: {
        marginBottom: theme.spacing(0.5)
      }
    }),
    { name: "ProductOrganization" }
  );

  const classes = useStyles(props);
  const intl = useIntl();

  const formErrors = getFormErrors(
    ["productType", "category", "collections"],
    errors
  );

  const DisplayCollections = collectionsInputDisplayValue.map(({ label }) => (
    <Typography>
      <Typography>{label}</Typography>{" "}
    </Typography>
  ));
  const [catID, setCatID] = useState("");

  const { data: catData } = useCatDetailsQuery({
    skip: catID === "" ? true : false,
    variables: { id: catID }
  });
  useEffect(() => {
    setCatID(data.category);
  }, [data?.category]);
  const displayCategoryCode = () => maybe(() => catData?.category.code, "");
  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Organize Product",
          description: "section header"
        })}
      />
      <CardContent>
        {canChangeType ? (
          <SingleAutocompleteSelectField
            displayValue={productTypeInputDisplayValue}
            error={!!formErrors.productType}
            helperText={getProductErrorMessage(formErrors.productType, intl)}
            name="productType"
            disabled={disabled}
            label={intl.formatMessage({
              defaultMessage: "Classification"
            })}
            choices={productTypes}
            value={data.productType?.id}
            onChange={onProductTypeChange}
            fetchChoices={fetchProductTypes}
            data-test="product-type"
            {...fetchMoreProductTypes}
          />
        ) : (
          <>
            <TextField
              InputProps={{ readOnly: true }}
              id="classificationTxt"
              fullWidth
              label={intl.formatMessage({
                defaultMessage: "Classification"
              })}
              name="productType"
              InputLabelProps={{
                shrink: true
              }}
              value={maybe(() => productType.name, "...") || ""}
            />
          </>
        )}
        <FormSpacer />
        {brand &&
        brand.name &&
        brand.legalName &&
        user.userType &&
        user.userType.code === "" ? (
          <TextField
            id="sellerTxt"
            InputProps={{ readOnly: true }}
            fullWidth
            label={intl.formatMessage({
              defaultMessage: "Seller"
            })}
            name="seller"
            InputLabelProps={{
              shrink: true
            }}
            value={
              maybe(() => brand.name) + " - " + maybe(() => brand.legalName)
            }
          />
        ) : (
          <Hr />
        )}
        <FormSpacer />
        {canChangeCategory ? (
          <SingleAutocompleteSelectField
            displayValue={categoryInputDisplayValue}
            error={!!formErrors.category || validate?.category}
            helperText={
              getProductErrorMessage(formErrors.category, intl) ||
              validate?.category
            }
            disabled={disabled || isStoreAdmin}
            label={intl.formatMessage({
              defaultMessage: "Category"
            })}
            choices={disabled ? [] : categories}
            name="category"
            value={data.category}
            onChange={onCategoryChange}
            fetchChoices={fetchCategories}
            data-test="category"
            {...fetchMoreCategories}
          />
        ) : (
          <TextField
            InputProps={{ readOnly: true }}
            id="categoryTxt"
            fullWidth
            label={intl.formatMessage({
              defaultMessage: "Category"
            })}
            name="category"
            InputLabelProps={{
              shrink: true
            }}
            value={maybe(() => categoryInputDisplayValue, "...") || ""}
          />
        )}
        {data.category ? (
          <div style={{ display: "block", marginTop: "10px" }}>
            <strong>Category Code: </strong>
            <p style={{ margin: 0 }} id="collectionValue">
              {displayCategoryCode()}
            </p>
          </div>
        ) : (
          ""
        )}
        <FormSpacer />
        <Hr />
        <FormSpacer />
        {isStoreAdmin || disabledCollection ? (
          <>
            <Typography className={classes.label} variant="caption">
              <FormattedMessage defaultMessage="Collections" />
            </Typography>
            {DisplayCollections}
          </>
        ) : (
          <MultiAutocompleteSelectField
            displayValues={collectionsInputDisplayValue}
            error={!!formErrors.collections}
            label={intl.formatMessage({
              defaultMessage: "Collections"
            })}
            choices={disabled ? [] : collections}
            name="collections"
            value={data.collections}
            helperText={
              getProductErrorMessage(formErrors.collections, intl) ||
              intl.formatMessage({
                defaultMessage:
                  "*Optional. Adding product to collection helps users find it.",
                description: "field is optional"
              })
            }
            onChange={onCollectionChange}
            fetchChoices={fetchCollections}
            data-test="collections"
            {...fetchMoreCollections}
          />
        )}
      </CardContent>
    </Card>
  );
};
ProductOrganization.displayName = "ProductOrganization";
export default ProductOrganization;
